import {Fragment, useEffect, useRef, useState} from 'react';
import {DialogTrigger, Root as DialogRoot} from '@radix-ui/react-dialog';
import {setElementVars} from '@vanilla-extract/dynamic';
import cx from 'classnames';
import NextLink from 'next/link';

import {Avatar, Icon, TallyLogo} from '#component/base';
import {SkipToContentButton} from '#component/partial/SkipToContentButton';
import {useThrottle, useViewportSize} from '#hook';

import {HeaderDropdown, HeaderItem, MenuItem, MenuSeparator, MobileMenu} from './components';
import {HeaderPromotionalBanner} from './components/HeaderPromotionalBanner';
import {miscLinks, navLinks} from './Header.util';

import {HeaderProps} from './Header.types';

import * as css from './Header.css';

export const Header = ({className, userData, variant = 'light'}: HeaderProps) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const isAuthenticated = !!userData;
  const hasActiveSubscription = !!userData?.hasActiveSubscription;
  const hasMembership = !!userData?.hasMembership;
  const isLightVariant = variant === 'light';
  const logoColor = isLightVariant ? 'burgundy' : 'ecruWhite';
  const iconColor = isLightVariant ? 'doveGray' : 'ecruWhite';
  const {isDesktop, isMobile} = useViewportSize();
  const featureFlagMap: Record<string, boolean> = {};

  const handleClickLink = () => {
    setOpen(false);
  };

  const handleScroll = () => {
    window.scrollY > 1 ? setScrolled(true) : setScrolled(false);
  };

  const throttledHandleScroll = useThrottle(handleScroll, 100, {leading: false, trailing: true});

  useEffect(() => {
    window.addEventListener('scroll', throttledHandleScroll);

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
    };
  });

  useEffect(() => {
    if (isDesktop) {
      setOpen(false);
    }
  }, [isDesktop]);

  const headerRef = useRef<HTMLElement>(null);

  const updateHeaderHeight = () => {
    if (headerRef.current) {
      const height = headerRef.current.offsetHeight;
      setElementVars(document.documentElement, {
        [css.headerHeightVar]: `${height}px`,
      });
    }
  };

  useEffect(() => {
    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);

    return () => {
      window.removeEventListener('resize', updateHeaderHeight);
    };
  }, []);

  const handleBannerLoad = () => {
    updateHeaderHeight();
  };

  return (
    <>
      <SkipToContentButton />
      <header className={cx(css.root({scrolled, variant}), className)} ref={headerRef}>
        <HeaderPromotionalBanner onLoad={handleBannerLoad} />
        <div className={css.header}>
          <NextLink href="/shop" passHref>
            <a aria-label="Tally Health™" className={css.logo}>
              <TallyLogo size={isMobile ? 40 : 48} sizeType="height" color={logoColor} />
            </a>
          </NextLink>

          <nav className={css.nav}>
            {navLinks.map(
              ({featureFlag, requiresAuth, requiresAnon, memberLock, subMenu, ...props}, index) => {
                if (
                  (requiresAuth === true && !isAuthenticated) ||
                  (requiresAnon === true && isAuthenticated) ||
                  (!!featureFlag && !featureFlagMap[featureFlag])
                ) {
                  return null;
                }

                return !!subMenu ? (
                  <HeaderDropdown trigger={props.children} key={index} variant={variant}>
                    {subMenu.map((props, innerIndex) => (
                      <MenuItem {...props} key={innerIndex} />
                    ))}
                  </HeaderDropdown>
                ) : (
                  <HeaderItem
                    {...props}
                    key={index}
                    variant={variant}
                    locked={memberLock && !hasMembership}
                  />
                );
              },
            )}
          </nav>

          <div className={css.navHeader}>
            {isAuthenticated ? (
              <HeaderDropdown
                trigger={
                  <Avatar
                    className={css.avatar}
                    aria-label="Open menu"
                    image={userData.avatar}
                    size="smaller"
                    text={userData.initials || '??'}
                  />
                }
                variant={variant}>
                {miscLinks.map(
                  (
                    {
                      requiresAuth,
                      requiresAnon,
                      requiresActiveSubscription,
                      requiresMembership,
                      hasSeparator,
                      hideOnDesktop,
                      featureFlag,
                      ...props
                    },
                    index,
                  ) => {
                    if (
                      (requiresAuth === true && !isAuthenticated) ||
                      (requiresAnon === true && isAuthenticated) ||
                      (requiresActiveSubscription === true && !hasActiveSubscription) ||
                      (requiresMembership === true && !hasMembership) ||
                      (!!featureFlag && !featureFlagMap[featureFlag])
                    ) {
                      return null;
                    }

                    return (
                      <Fragment key={index}>
                        {hasSeparator && <MenuSeparator />}
                        <MenuItem {...props} />
                      </Fragment>
                    );
                  },
                )}
              </HeaderDropdown>
            ) : (
              <HeaderItem href="/signin" variant={variant}>
                Log in
              </HeaderItem>
            )}
          </div>
          <div className={css.mobileNav}>
            <HeaderItem href="/shop/collections" variant={variant}>
              Shop
            </HeaderItem>

            <DialogRoot open={open} onOpenChange={setOpen}>
              <DialogTrigger className={css.menuButton}>
                <Icon aria-label="Open menu" color={iconColor} name="Equals" size={24} />
              </DialogTrigger>

              <MobileMenu
                avatar={userData?.avatar}
                initials={userData?.initials}
                isAuthenticated={isAuthenticated}
                hasActiveSubscription={hasActiveSubscription}
                hasMembership={hasMembership}
                featureFlagMap={featureFlagMap}
                onClickLink={handleClickLink}
              />
            </DialogRoot>
          </div>
        </div>
      </header>
    </>
  );
};
